import { cx } from './cx';
import { Text } from './Text';

export type CircularGaugeTone = 'success' | 'warning' | 'error' | 'brand';

export interface CircularGaugeThresholds {
  warning: number;
  error: number;
}

export interface CircularGaugeProps {
  /** 0–100. */
  value: number;
  /** Diameter in px. */
  size?: number;
  /** Caption rendered under the number. */
  label?: string;
  /** Forces the arc color; omit to derive it from `thresholds`. */
  tone?: CircularGaugeTone;
  /** Derives `tone` from `value` when `tone` is omitted: < warning → success, < error → warning, else error. */
  thresholds?: CircularGaugeThresholds;
  className?: string;
}

const toneStrokeClasses: Record<CircularGaugeTone, string> = {
  success: 'stroke-success',
  warning: 'stroke-warning',
  error: 'stroke-error',
  brand: 'stroke-brand-solid',
};

/** Shared with callers that pair a gauge with a `ProgressBar` on the same metric. */
export function deriveGaugeTone(
  value: number,
  tone: CircularGaugeTone | undefined,
  thresholds: CircularGaugeThresholds | undefined,
): CircularGaugeTone {
  if (tone) return tone;
  if (thresholds) {
    if (value < thresholds.warning) return 'success';
    if (value < thresholds.error) return 'warning';
    return 'error';
  }
  return 'brand';
}

const STROKE_RATIO = 0.09;

/**
 * Radial progress gauge — a muted track ring plus a colored progress arc, with
 * the value centered. Reads better than a linear bar for a single hero metric
 * (CPU, disk) where the eye should land on one number.
 */
export const CircularGauge = ({
  value,
  size = 96,
  label,
  tone,
  thresholds,
  className,
}: CircularGaugeProps) => {
  const pct = Math.max(0, Math.min(100, value));
  const resolvedTone = deriveGaugeTone(pct, tone, thresholds);
  const strokeWidth = Math.max(4, size * STROKE_RATIO);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - pct / 100);
  const center = size / 2;

  return (
    <div
      className={cx(
        'relative inline-flex shrink-0 items-center justify-center',
        className,
      )}
      style={{ width: size, height: size }}
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90"
        aria-hidden="true"
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className="text-fg-muted"
          stroke="currentColor"
          opacity={0.2}
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={cx(
            'transition-[stroke-dashoffset] duration-300',
            toneStrokeClasses[resolvedTone],
          )}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
        <Text
          variant="body"
          mono
          as="span"
          className="text-lg font-semibold leading-none text-fg"
        >
          {Math.round(pct)}%
        </Text>
        {label && (
          <Text
            variant="caption"
            tone="tertiary"
            as="span"
            className="leading-none"
          >
            {label}
          </Text>
        )}
      </div>
    </div>
  );
};
